// src/services/userViews.service.ts
// Servicio para gestionar las vistas permitidas por usuario (administración de permisos de vistas)

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

/**
 * Definición de todas las vistas disponibles en el sistema
 */
export const AVAILABLE_VIEWS = [
  { id: 'dashboard', label: 'Dashboard', path: '/quality/dashboard', description: 'Panel principal con métricas y estadísticas' },
  { id: 'documents', label: 'Documentos', path: '/quality/documents', description: 'Gestión de documentos del sistema de calidad' },
  { id: 'approvals', label: 'Solicitudes', path: '/quality/approvals', description: 'Gestión de solicitudes y aprobaciones' },
  { id: 'my_documents', label: 'Mis Documentos', path: '/creator/dashboard', description: 'Documentos creados por el usuario' },
  { id: 'master_list', label: 'Lista Maestra Documentos', path: '/documents/view', description: 'Vista de lectura de todos los documentos' },
  { id: 'app_change_control', label: 'Control de Cambios App', path: '/admin/app-change-control', description: 'Control de versiones de la aplicación' },
  { id: 'users', label: 'Control de Usuarios', path: '/admin/users', description: 'Gestión de usuarios del sistema' },
  { id: 'whatsapp', label: 'Notificaciones WhatsApp', path: '/admin/whatsapp', description: 'Configuración de notificaciones WhatsApp' },
  { id: 'user_views', label: 'Gestión de Usuarios', path: '/admin/user-views', description: 'Gestión de permisos de vistas por usuario' },
  { id: 'app_settings', label: 'Configuración', path: '/admin/settings', description: 'Configuración general de la aplicación' },
  // Auditorías
  { id: 'auditoria_interna', label: '1ra Parte (Interna)', path: '/auditoria/interna', description: 'Auditoría interna de primera parte' },
  { id: 'auditoria_proveedor', label: '2da Parte (Proveedor/Cliente)', path: '/auditoria/proveedor-cliente', description: 'Auditoría de segunda parte a proveedor o cliente' },
  { id: 'auditoria_certificacion', label: '3ra Parte (Certificación)', path: '/auditoria/certificacion', description: 'Auditoría de tercera parte para certificación' },
  // Revisión Gerencial
  { id: 'monitoreo_indicadores', label: 'Monitoreo de Indicadores', path: '/revision-gerencial/monitoreo', description: 'Monitoreo de indicadores de calidad' },
  { id: 'acciones_gerencial', label: 'Acciones', path: '/revision-gerencial/acciones', description: 'Acciones de revisión gerencial' },
  { id: 'minuta_gerencial', label: 'Minuta Gerencial', path: '/revision-gerencial/minuta', description: 'Minutas de revisión gerencial' },
  // Certificado
  { id: 'certificado_iatf', label: 'Certificado IATF', path: '/certificado/iatf', description: 'Certificado IATF vigente' },
  // Análisis de Riesgos
  { id: 'riesgos_matriz', label: 'Matriz de Análisis de Riesgos', path: '/analisis-riesgos/matriz', description: 'Matriz de identificación y análisis de riesgos' },
  { id: 'riesgos_tratamiento', label: 'Tratamiento del Riesgo', path: '/analisis-riesgos/tratamiento', description: 'Plan de tratamiento de riesgos identificados' },
  { id: 'riesgos_monitoreo', label: 'Monitoreo y Revisión', path: '/analisis-riesgos/monitoreo', description: 'Seguimiento y revisión de riesgos' },
  // Plan de Contingencia
  { id: 'contingencia_programa', label: 'Programa de Plan de Contingencia', path: '/plan-contingencia/programa', description: 'Programa general del plan de contingencia' },
  { id: 'contingencia_simulaciones', label: 'Simulaciones', path: '/plan-contingencia/simulaciones', description: 'Registro de simulaciones del plan de contingencia' },
  { id: 'contingencia_resultados', label: 'Resultados', path: '/plan-contingencia/resultados', description: 'Resultados de simulaciones y activaciones' },
  // Requisitos Específicos de Clientes
  { id: 'clientes_encuestas', label: 'Encuestas de Satisfacción', path: '/requisitos-clientes/encuestas', description: 'Encuestas de satisfacción de clientes' },
  { id: 'clientes_concentrado', label: 'Concentrado de Requerimientos', path: '/requisitos-clientes/concentrado', description: 'Concentrado de requerimientos específicos de clientes' },
  { id: 'clientes_difusion', label: 'Difusión', path: '/requisitos-clientes/difusion', description: 'Difusión de requisitos específicos de clientes' },
  // Mejoras
  { id: 'mejoras_procesos', label: 'Procesos', path: '/mejoras/procesos', description: 'Mejoras en procesos' },
  { id: 'mejoras_documentacion', label: 'Documentación', path: '/mejoras/documentacion', description: 'Mejoras en documentación' },
  { id: 'mejoras_personal', label: 'Personal', path: '/mejoras/personal', description: 'Mejoras relacionadas con el personal' },
  { id: 'mejoras_indicadores', label: 'Indicadores', path: '/mejoras/indicadores', description: 'Mejoras en indicadores' },
  { id: 'mejoras_cliente', label: 'Cliente', path: '/mejoras/cliente', description: 'Mejoras orientadas al cliente' },
  // Material Didáctico
  { id: 'material_didactico', label: 'Material Didáctico', path: '/material-didactico', description: 'Recursos y material didáctico del sistema' },
] as const;

export type ViewId = typeof AVAILABLE_VIEWS[number]['id'];

/**
 * Permisos de vistas para un usuario
 */
export interface UserViewPermissions {
  uid: string;
  allowedViews: ViewId[];
  updatedAt: Date;
  updatedBy: string;
}

/**
 * Vista resumida de un usuario con sus permisos
 */
export interface UserWithViews {
  uid: string;
  fullName: string;
  email: string;
  nomina: string;
  roleSGC: string;
  accesoSGC: boolean;
  allowedViews: ViewId[];
}

const COLLECTION_NAME = 'userViewPermissions';
const ROLE_DEFAULTS_DOC = 'roleDefaultViews';

const HARDCODED_ROLE_DEFAULTS: Record<string, ViewId[]> = {
  Administrador_Calidad: AVAILABLE_VIEWS.map((v) => v.id) as ViewId[],
  Responsable_Calidad:   ['dashboard', 'documents', 'approvals', 'master_list'],
  Auditor_Interno:       ['auditoria_interna', 'auditoria_proveedor', 'auditoria_certificacion', 'master_list'],
  Creador_Documentos:    ['my_documents', 'master_list'],
  Aprobador:             ['master_list'],
  Revisor:               ['master_list'],
  Lector:                ['master_list'],
};

export { HARDCODED_ROLE_DEFAULTS };

class UserViewsService {
  private col = COLLECTION_NAME;
  private roleDefaultsCache: Record<string, ViewId[]> = { ...HARDCODED_ROLE_DEFAULTS };

  /**
   * Obtiene los permisos de vistas de un usuario específico
   */
  async getUserPermissions(uid: string): Promise<UserViewPermissions | null> {
    const docRef = doc(db, this.col, uid);
    const snap = await getDoc(docRef);
    
    if (!snap.exists()) {
      return null;
    }
    
    const data = snap.data();
    return {
      uid: snap.id,
      allowedViews: data.allowedViews || [],
      updatedAt: data.updatedAt?.toDate() || new Date(),
      updatedBy: data.updatedBy || '',
    };
  }

  /**
   * Establece los permisos de vistas para un usuario
   */
  async setUserPermissions(
    uid: string, 
    allowedViews: ViewId[], 
    updatedBy: string
  ): Promise<void> {
    const docRef = doc(db, this.col, uid);
    const data = {
      allowedViews,
      updatedAt: new Date(),
      updatedBy,
    };
    
    await setDoc(docRef, data, { merge: true });
  }

  /**
   * Actualiza parcialmente los permisos de un usuario
   */
  async updateUserPermissions(
    uid: string, 
    updates: Partial<Pick<UserViewPermissions, 'allowedViews'>>,
    updatedBy: string
  ): Promise<void> {
    const docRef = doc(db, this.col, uid);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
      updatedBy,
    });
  }

  /**
   * Elimina los permisos de vistas de un usuario
   */
  async deleteUserPermissions(uid: string): Promise<void> {
    const docRef = doc(db, this.col, uid);
    await deleteDoc(docRef);
  }

  /**
   * Obtiene todos los usuarios con sus permisos de vistas
   */
  async getAllUsersWithViews(): Promise<UserWithViews[]> {
    // Obtener todos los usuarios
    const usersSnap = await getDocs(collection(db, 'users'));
    
    // Obtener todos los permisos
    const permissionsSnap = await getDocs(collection(db, this.col));
    
    // Crear mapa de permisos por uid
    const permissionsMap = new Map<string, ViewId[]>();
    permissionsSnap.docs.forEach((doc) => {
      const data = doc.data();
      permissionsMap.set(doc.id, data.allowedViews || []);
    });
    
    // Combinar datos
    return usersSnap.docs.map((doc) => {
      const data = doc.data();
      return {
        uid: doc.id,
        fullName: data.fullName || '',
        email: data.email || '',
        nomina: data.nomina || '',
        roleSGC: data.roleSGC || 'Lector',
        accesoSGC: data.accesoSGC === true,
        allowedViews: permissionsMap.get(doc.id) || [],
      };
    });
  }

  /**
   * Suscribe a cambios en los permisos de un usuario específico (tiempo real)
   */
  subscribeToUserPermissions(
    uid: string,
    callback: (permissions: UserViewPermissions | null) => void
  ): () => void {
    const docRef = doc(db, this.col, uid);
    
    return onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        
        const data = snap.data();
        callback({
          uid: snap.id,
          allowedViews: data.allowedViews || [],
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy || '',
        });
      },
      (error) => {
        console.error('Error subscribing to user permissions:', error);
        callback(null);
      }
    );
  }

  /**
   * Suscribe a cambios en todos los permisos de vistas (tiempo real)
   */
  subscribeToAllPermissions(
    callback: (permissions: Map<string, ViewId[]>) => void
  ): () => void {
    const colRef = collection(db, this.col);
    
    return onSnapshot(
      colRef,
      (snap) => {
        const permissionsMap = new Map<string, ViewId[]>();
        snap.docs.forEach((doc) => {
          const data = doc.data();
          permissionsMap.set(doc.id, data.allowedViews || []);
        });
        callback(permissionsMap);
      },
      (error) => {
        console.error('Error subscribing to all permissions:', error);
        callback(new Map());
      }
    );
  }

  /**
   * Obtiene las vistas disponibles para un rol específico (cache en memoria, sincronizado con Firestore)
   */
  getDefaultViewsForRole(role: string): ViewId[] {
    return this.roleDefaultsCache[role] ?? ['master_list'];
  }

  /**
   * Suscribe a cambios en los defaults de rol en tiempo real
   */
  subscribeToRoleDefaults(
    callback: (defaults: Record<string, ViewId[]>) => void
  ): () => void {
    const docRef = doc(db, 'appConfig', ROLE_DEFAULTS_DOC);
    return onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          // Mezclar hardcoded con Firestore (Firestore gana)
          this.roleDefaultsCache = { ...HARDCODED_ROLE_DEFAULTS };
          for (const [role, views] of Object.entries(data)) {
            if (!role.startsWith('_') && Array.isArray(views)) {
              this.roleDefaultsCache[role] = views as ViewId[];
            }
          }
        } else {
          this.roleDefaultsCache = { ...HARDCODED_ROLE_DEFAULTS };
        }
        callback({ ...this.roleDefaultsCache });
      },
      (error) => {
        console.error('Error subscribing to role defaults:', error);
        callback({ ...this.roleDefaultsCache });
      }
    );
  }

  /**
   * Guarda los defaults de vistas para un rol en Firestore
   */
  async saveRoleDefaults(role: string, views: ViewId[], updatedBy: string): Promise<void> {
    const docRef = doc(db, 'appConfig', ROLE_DEFAULTS_DOC);
    await setDoc(docRef, {
      [role]: views,
      _updatedAt: new Date(),
      _updatedBy: updatedBy,
    }, { merge: true });
    this.roleDefaultsCache[role] = views;
  }

  /**
   * Retorna los defaults del sistema (hardcoded) sin Firestore
   */
  getSystemDefaults(): Record<string, ViewId[]> {
    return { ...HARDCODED_ROLE_DEFAULTS };
  }

  /**
   * Verifica si un usuario tiene acceso a una vista específica
   */
  hasViewAccess(allowedViews: ViewId[] | undefined, viewId: ViewId): boolean {
    if (!allowedViews || allowedViews.length === 0) {
      return false;
    }
    return allowedViews.includes(viewId);
  }

  /**
   * Obtiene la información de una vista por su ID
   */
  getViewById(viewId: ViewId): typeof AVAILABLE_VIEWS[number] | undefined {
    return AVAILABLE_VIEWS.find((v) => v.id === viewId);
  }

  /**
   * Obtiene la información de una vista por su path
   */
  getViewByPath(path: string): typeof AVAILABLE_VIEWS[number] | undefined {
    return AVAILABLE_VIEWS.find((v) => v.path === path);
  }
}

export const userViewsService = new UserViewsService();
